
import React, { useState } from 'react';
import { User, UserRole, Vote } from '../types';
import { MOCK_VOTES } from '../mockData';
import { Vote as VoteIcon, CheckCircle2, Users, Clock, AlertCircle, Plus, X, Trash2, Calendar as CalendarIcon } from 'lucide-react';

interface VotingProps {
  user: User;
}

const Voting: React.FC<VotingProps> = ({ user }) => {
  const [votes, setVotes] = useState<Vote[]>(MOCK_VOTES);
  const [isFormOpen, setIsFormOpen] = useState(false);
  
  // Form state
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState<string[]>(['', '']);
  const [expiryDate, setExpiryDate] = useState('');

  const isClosed = (expiresAt: string) => {
    return new Date(expiresAt) < new Date();
  };

  const handleVote = (voteId: string, option: string) => {
    setVotes(prev => prev.map(v => {
      if (v.id === voteId) {
        return {
          ...v,
          results: {
            ...v.results,
            [option]: (v.results[option] || 0) + 1
          },
          votedBy: [...v.votedBy, user.id]
        };
      }
      return v;
    }));
  };

  const addOptionField = () => {
    setOptions([...options, '']);
  };

  const removeOptionField = (index: number) => {
    if (options.length > 2) {
      const newOptions = [...options];
      newOptions.splice(index, 1);
      setOptions(newOptions);
    }
  };

  const updateOptionValue = (index: number, value: string) => {
    const newOptions = [...options];
    newOptions[index] = value;
    setOptions(newOptions);
  };

  const handleCreatePoll = () => {
    if (!question || options.some(opt => !opt) || !expiryDate) return;

    const initialResults: Record<string, number> = {};
    options.forEach(opt => {
      initialResults[opt] = 0;
    });

    const newVote: Vote = {
      id: `v-${Date.now()}`,
      question,
      options,
      results: initialResults,
      expiresAt: expiryDate,
      votedBy: []
    };

    setVotes([newVote, ...votes]);
    setIsFormOpen(false);
    // Reset form
    setQuestion('');
    setOptions(['', '']);
    setExpiryDate('');
  };

  const isAdmin = user.role === UserRole.PRESIDENT || user.role === UserRole.DEPUTY || user.role === UserRole.SUPER_ADMIN;

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Гласање и анкети</h1>
          <p className="text-slate-500">Учествувајте во одлуките на заедницата.</p>
        </div>
        {isAdmin && (
          <button 
            onClick={() => setIsFormOpen(true)}
            className="bg-indigo-600 text-white px-4 py-2 rounded-xl font-semibold hover:bg-indigo-700 transition-all flex items-center shadow-lg shadow-indigo-100"
          >
            <Plus className="w-5 h-5 mr-2" /> Креирај анкета
          </button>
        )}
      </header>

      {isFormOpen && (
        <div className="bg-white p-8 rounded-3xl border border-indigo-100 shadow-xl space-y-6 animate-in slide-in-from-top duration-300">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-bold text-slate-900 flex items-center">
              <Plus className="w-5 h-5 mr-2 text-indigo-600" /> Нова анкета
            </h3>
            <button onClick={() => setIsFormOpen(false)} className="text-slate-400 hover:text-slate-600">
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1 block">Прашање</label>
              <textarea 
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder="На пр. Дали да се реновира покривот?"
                className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl focus:ring-2 focus:ring-indigo-500 outline-none font-semibold text-slate-800"
              />
            </div>

            <div className="space-y-3">
              <label className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1 block">Опции за гласање</label>
              {options.map((opt, idx) => (
                <div key={idx} className="flex items-center space-x-2">
                  <input 
                    type="text" 
                    value={opt}
                    onChange={(e) => updateOptionValue(idx, e.target.value)}
                    placeholder={`Опција ${idx + 1}`}
                    className="flex-1 p-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none"
                  />
                  {options.length > 2 && (
                    <button 
                      onClick={() => removeOptionField(idx)}
                      className="p-2 text-slate-400 hover:text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
              <button 
                onClick={addOptionField}
                className="text-indigo-600 text-sm font-bold flex items-center hover:bg-indigo-50 px-3 py-2 rounded-lg transition-colors"
              >
                <Plus className="w-4 h-4 mr-1" /> Додај уште една опција
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1 block">Датум на завршување</label>
                <div className="relative">
                  <CalendarIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input 
                    type="date" 
                    value={expiryDate}
                    onChange={(e) => setExpiryDate(e.target.value)}
                    className="w-full p-3 pl-11 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button onClick={() => setIsFormOpen(false)} className="px-5 py-2 text-slate-500 font-bold">Откажи</button>
            <button 
              onClick={handleCreatePoll}
              disabled={!question || options.some(opt => !opt) || !expiryDate}
              className="bg-indigo-600 text-white px-8 py-2 rounded-xl font-bold shadow-lg shadow-indigo-100 hover:bg-indigo-700 transition-all disabled:opacity-50"
            >
              Креирај анкета
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6">
        {votes.map(vote => {
          const totalVotes = Object.values(vote.results).reduce((a: number, b: number) => a + b, 0);
          const closed = isClosed(vote.expiresAt);
          const userAlreadyVoted = vote.votedBy.includes(user.id);

          return (
            <div key={vote.id} className={`bg-white rounded-3xl border p-8 shadow-sm transition-all ${closed ? 'opacity-75 grayscale-[0.5] border-slate-200' : 'border-slate-200 hover:border-indigo-300'}`}>
              <div className="flex items-start justify-between mb-8">
                <div>
                  <div className="flex items-center space-x-3 mb-3">
                    <div className={`flex items-center space-x-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${closed ? 'bg-slate-100 text-slate-500' : 'bg-emerald-50 text-emerald-600'}`}>
                      {closed ? <AlertCircle className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}
                      <span>{closed ? 'Затворено' : 'Активно'}</span>
                    </div>
                    <span className="text-xs font-bold text-slate-400">• {(totalVotes as number)} гласови досега</span>
                  </div>
                  <h2 className="text-2xl font-bold text-slate-900 leading-tight">{vote.question}</h2>
                </div>
                <div className="text-right">
                  <span className="block text-[10px] text-slate-400 font-bold uppercase tracking-widest mb-1">{closed ? 'Завршено на' : 'Завршува на'}</span>
                  <span className={`text-sm font-bold ${closed ? 'text-slate-500' : 'text-indigo-600'}`}>{vote.expiresAt}</span>
                </div>
              </div>

              <div className="space-y-4">
                {vote.options.map(option => {
                  const count = vote.results[option] || 0;
                  const percentage = (totalVotes as number) > 0 ? (count / (totalVotes as number)) * 100 : 0;
                  const canVote = !closed && !userAlreadyVoted && user.role === UserRole.TENANT;

                  return (
                    <div key={option} className="group relative">
                      <button 
                        disabled={!canVote}
                        onClick={() => handleVote(vote.id, option)}
                        className={`w-full text-left p-5 rounded-2xl border transition-all relative overflow-hidden group/btn ${
                          canVote 
                          ? 'border-slate-200 hover:border-indigo-400 bg-white' 
                          : 'border-slate-100 bg-slate-50/50 cursor-default'
                        }`}
                      >
                        <div className="relative z-10 flex items-center justify-between mb-2">
                          <span className={`font-bold ${canVote ? 'text-slate-700' : 'text-slate-400'}`}>{option}</span>
                          <div className="flex items-center space-x-2">
                            <span className="text-xs font-bold text-slate-400">{count} гласови</span>
                            <span className={`text-sm font-black ${canVote ? 'text-indigo-600' : 'text-slate-400'}`}>{Math.round(percentage)}%</span>
                          </div>
                        </div>
                        <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden relative z-10">
                          <div 
                            className={`h-full transition-all duration-1000 ease-out ${closed ? 'bg-slate-400' : 'bg-indigo-500'}`} 
                            style={{ width: `${percentage}%` }} 
                          />
                        </div>
                      </button>
                    </div>
                  );
                })}
              </div>

              <div className="mt-8 flex items-center justify-between pt-6 border-t border-slate-50">
                <div className="flex items-center space-x-6 text-slate-400 text-[10px] font-bold uppercase tracking-widest">
                  <div className="flex items-center">
                    <Users className="w-4 h-4 mr-2 text-slate-300" />
                    <span>{(totalVotes as number)} учесници</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle2 className="w-4 h-4 mr-2 text-slate-300" />
                    <span>Анонимно гласање</span>
                  </div>
                </div>
                
                {closed ? (
                  <div className="text-slate-400 font-bold text-sm italic">Анкетата е завршена</div>
                ) : userAlreadyVoted ? (
                  <div className="flex items-center text-emerald-600 font-bold text-sm">
                    <CheckCircle2 className="w-4 h-4 mr-2" /> Вие гласавте
                  </div>
                ) : user.role === UserRole.TENANT ? (
                  <div className="text-indigo-400 text-xs font-bold italic animate-pulse">Изберете опција за да гласате</div>
                ) : (
                  <button className="text-indigo-600 font-bold hover:underline text-sm px-4 py-2 bg-indigo-50 rounded-lg">Види детален извештај</button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Voting;
